// src/pages/driver-recharge/model/on-recharge-submit.ts
import { useEffect } from 'react'
import { createWidget } from '@sber-ecom-core/sberpay-widget'
import { type UseMutateAsyncFunction, useQueryClient } from '@tanstack/react-query'
import { useLocation, useNavigate } from '@tanstack/react-router'
import type { SubmitHandler, UseFormReturn } from 'react-hook-form'
import { toast } from 'sonner'

import {
    type CreateSberPayPaymentMutationRequest,
    type CreateTBankPaymentMutationRequest,
    type CreateTBankSBPPaymentMutationRequest,
    type CreateWalletOnePaymentMutationRequest,
    type ErrorResponse,
    getSBPPayloadQueryOptions,
    type InitPaymentResponse,
    type InitSBPPaymentResponse,
    type PaymentMethod,
    sberPayPaymentRequestDeviceOsEnum,
    type SberPayPaymentResponse,
    type WalletOnePaymentResponse,
} from '@/shared/api/generated'
import {
    setLocalStorageValue,
    type TDriverRechargeOnboardingKeys,
} from '@/shared/hooks/use-local-storage'
import { isAndroid, isIos } from '@/shared/lib/app-bridge'
import { currencyParser } from '@/shared/lib/currency-parser'
import { AppsflyerEvent, sendAppsflyer } from '@/shared/metrics'
import { ErrorToast } from '@/shared/ui'

import { markPaymentRedirect } from './payment-redirect'
import type { TFormValues } from './types'

import { useMetrics } from '.'

type TUseOnRechargeSubmitProps = {
    formMethods: UseFormReturn<TFormValues>
    maxBalance: number
    mutateAsyncSberPay: UseMutateAsyncFunction<
        SberPayPaymentResponse,
        ErrorResponse,
        {
            data: CreateSberPayPaymentMutationRequest
        },
        unknown
    >
    mutateAsyncTBank: UseMutateAsyncFunction<
        InitPaymentResponse,
        ErrorResponse,
        {
            data: CreateTBankPaymentMutationRequest
        },
        unknown
    >
    mutateAsyncTBankSBP: UseMutateAsyncFunction<
        InitSBPPaymentResponse,
        ErrorResponse,
        {
            data: CreateTBankSBPPaymentMutationRequest
        },
        unknown
    >
    mutateAsyncWalletOne: UseMutateAsyncFunction<
        WalletOnePaymentResponse,
        ErrorResponse,
        {
            data: CreateWalletOnePaymentMutationRequest
        },
        unknown
    >
    openModalAmount: () => void
    setIsRedirecting: (v: boolean) => void
    walletInfoBalance: number
}

export const useOnRechargeSubmit = ({
    formMethods,
    walletInfoBalance,
    maxBalance,
    openModalAmount,
    setIsRedirecting,
    mutateAsyncTBank,
    mutateAsyncTBankSBP,
    mutateAsyncSberPay,
    mutateAsyncWalletOne,
}: TUseOnRechargeSubmitProps): SubmitHandler<TFormValues> => {
    const navigation = useNavigate()
    const { state } = useLocation().search
    const queryClient = useQueryClient()

    // Метрики
    const { financeTopupDoneClickEvent, financeTopupDoneRedirectEvent } = useMetrics({
        formMethods,
    })

    // Функция для успешного завершения пополнения
    const successFunction = (data: TFormValues) => {
        setLocalStorageValue<TDriverRechargeOnboardingKeys>('driver-recharge-onboarding', 'showed')
        setLocalStorageValue<string>('driver-recharge-amount', data.value)
        setLocalStorageValue<PaymentMethod>('driver-recharge-selected-method', data.paymentMethod)
        sendAppsflyer(AppsflyerEvent.finance_topup_done_click, financeTopupDoneClickEvent())
    }
    // Создание виджета SberPay
    const widget = createWidget(__GLOBAL__.VITE_SBERPAY_ENV as 'IFT' | 'PRODUCTION' | 'UAT')

    // Редиректы для SberPay
    useEffect(() => {
        switch (state) {
            case 'success': {
                setIsRedirecting(true)
                navigation({ to: '/driver-recharge/success', replace: true })

                return
            }
            case 'cancel': {
                setIsRedirecting(true)
                navigation({
                    to: '/driver-recharge/error',
                    replace: true,
                    state: { error: 'Клиент нажал на кнопку «Отменить»' } as Record<
                        string,
                        unknown
                    >,
                })

                return
            }
            default: {
                setIsRedirecting(false)
            }
        }
    }, [state, setIsRedirecting, navigation])

    return async (data) => {
        setIsRedirecting(true)
        const amount = Number(data.value)

        // Проверка на максимальный баланс
        if (amount + walletInfoBalance > maxBalance) {
            sendAppsflyer(
                AppsflyerEvent.finance_topup_done_click,
                financeTopupDoneClickEvent('error_limit'),
            )
            toast(
                <ErrorToast
                    text={`На счёте может быть не больше ${currencyParser(maxBalance)} ₽`}
                />,
                {
                    duration: 3000,
                    position: 'bottom-center',
                },
            )
            setIsRedirecting(false)

            return
        }

        // Проверка на минимальный и максимальный лимиты
        if (amount < data.paymentMethod.limits.min || amount > data.paymentMethod.limits.max) {
            openModalAmount()
            setIsRedirecting(false)

            return
        }

        try {
            switch (data.paymentMethod.id) {
                case 'tbank-sbp': {
                    try {
                        const res = await mutateAsyncTBankSBP({
                            data: {
                                amount: Number(data.value),
                                description: 'Пополнение баланса водителя',
                                language: 'ru',
                                success_url:
                                    globalThis.location.origin +
                                    '/redirect?redirectUrl=driver-recharge-success',
                                fail_url:
                                    globalThis.location.origin +
                                    '/redirect?redirectUrl=driver-recharge-error',
                            },
                        })
                        const payload = await queryClient.fetchQuery(
                            getSBPPayloadQueryOptions({ payment_id: Number(res.ext_order_id) }),
                        )

                        if (!payload?.url) {
                            setIsRedirecting(false)

                            navigation({
                                to: 'error',
                                replace: true,
                                state: { error: 'Не удалось получить ссылку для оплаты' } as Record<
                                    string,
                                    unknown
                                >,
                            })

                            return
                        }
                        successFunction(data)
                        sendAppsflyer(
                            AppsflyerEvent.finance_topup_done_redirect,
                            financeTopupDoneRedirectEvent(res.payment_id),
                        )
                        markPaymentRedirect()
                        globalThis.location.href = payload.url

                        return
                    } catch (error) {
                        setIsRedirecting(false)
                        navigation({
                            to: 'error',
                            replace: true,
                            state: {
                                error: (error as { error?: string })?.error ?? 'Неизвестная ошибка',
                            } as Record<string, unknown>,
                        })

                        return
                    }
                }
                case 'sberpay': {
                    await mutateAsyncSberPay({
                        data: {
                            amount,
                            device_os:
                                isIos() && !isAndroid() ?
                                    sberPayPaymentRequestDeviceOsEnum.ios
                                :   sberPayPaymentRequestDeviceOsEnum.android,
                            back_url: globalThis.location.origin + '/driver-recharge',
                        },
                    })
                        .then((res) => {
                            const params = {
                                bankInvoiceId: res.ext_order_id,
                                backUrl: res.back_url,
                            }

                            successFunction(data)
                            sendAppsflyer(
                                AppsflyerEvent.finance_topup_done_redirect,
                                financeTopupDoneRedirectEvent(res.payment_id),
                            )
                            widget.open(params)
                        })
                        .catch((error) => {
                            setIsRedirecting(false)
                            navigation({
                                to: 'error',
                                replace: true,
                                state: {
                                    error: error.error ?? 'Неизвестная ошибка',
                                } as Record<string, unknown>,
                            })
                        })

                    return
                }
                case 'wallet-one': {
                    await mutateAsyncWalletOne({
                        data: {
                            amount,
                            success_url:
                                globalThis.location.origin +
                                '/redirect?redirectUrl=driver-recharge-success',
                            fail_url:
                                globalThis.location.origin +
                                '/redirect?redirectUrl=driver-recharge-error',
                        },
                    })
                        .then((res) => {
                            const form = document.createElement('form')

                            form.method = 'POST'
                            form.action = res.action.url
                            form.setAttribute('accept-charset', 'utf8')

                            const wmiFields = res.wmi_fields

                            for (const [key, value] of Object.entries(wmiFields)) {
                                if (Array.isArray(value)) {
                                    for (const valueKey in value) {
                                        const fieldValue = value[valueKey]
                                        const input = document.createElement('input')

                                        input.type = 'hidden'
                                        input.name = key
                                        input.value = fieldValue == null ? '' : String(fieldValue)
                                        form.append(input)
                                    }
                                } else {
                                    const fieldValue = value
                                    const input = document.createElement('input')

                                    input.type = 'hidden'
                                    input.name = key
                                    input.value = fieldValue == null ? '' : String(fieldValue)
                                    form.append(input)
                                }
                            }
                            document.body.append(form)
                            successFunction(data)
                            sendAppsflyer(
                                AppsflyerEvent.finance_topup_done_redirect,
                                financeTopupDoneRedirectEvent(res.payment_id),
                            )
                            markPaymentRedirect()
                            form.submit()
                            form.remove()
                        })
                        .catch((error) => {
                            setIsRedirecting(false)
                            navigation({
                                to: 'error',
                                replace: true,
                                state: {
                                    error: error.error ?? 'Неизвестная ошибка',
                                } as Record<string, unknown>,
                            })
                        })

                    return
                }
                case 'tbank': {
                    await mutateAsyncTBank({
                        data: {
                            amount: Number(data.value),
                            description: 'Пополнение баланса водителя',
                            language: 'ru',
                            success_url:
                                globalThis.location.origin +
                                '/redirect?redirectUrl=driver-recharge-success',
                            fail_url:
                                globalThis.location.origin +
                                '/redirect?redirectUrl=driver-recharge-error',
                        },
                    })
                        .then((res) => {
                            successFunction(data)
                            sendAppsflyer(
                                AppsflyerEvent.finance_topup_done_redirect,
                                financeTopupDoneRedirectEvent(res.payment_id),
                            )
                            markPaymentRedirect()
                            globalThis.location.href = res.payment_url
                        })
                        .catch((error) => {
                            setIsRedirecting(false)
                            navigation({
                                to: 'error',
                                replace: true,
                                state: {
                                    error: error.error ?? 'Неизвестная ошибка',
                                } as Record<string, unknown>,
                            })
                        })

                    return
                }
                default: {
                    setIsRedirecting(false)
                    toast(
                        <ErrorToast
                            text={`Нет способа оплаты с id = ${formMethods.watch('paymentMethod').id}`}
                        />,
                        {
                            duration: 3000,
                            position: 'bottom-center',
                        },
                    )
                    sendAppsflyer(
                        AppsflyerEvent.finance_topup_done_click,
                        financeTopupDoneClickEvent('unknown_payment_method'),
                    )
                }
            }
        } catch (error) {
            setIsRedirecting(false)
            toast(
                <ErrorToast text={error instanceof Error ? error.message : 'Неизвестная ошибка'} />,
                {
                    duration: 3000,
                    position: 'bottom-center',
                },
            )
            sendAppsflyer(
                AppsflyerEvent.finance_topup_done_click,
                financeTopupDoneClickEvent('unknown_error'),
            )
        }
    }
}
